"use client";

import { useRef, useEffect, useState } from "react";
import { Magnetic } from "@/components/ui/Magnetic";
import { Clock } from "@/components/ui/Clock";
import { MiniCube } from "@/components/ui/MiniCube";
import { useMotionMode } from "@/context/MotionContext";
import { PulseDot } from "@/components/ui/PulseDot";

// Build version info from environment
const VERSION = process.env.NEXT_PUBLIC_APP_VERSION || "0.1.0";
const GIT_COMMIT = process.env.NEXT_PUBLIC_GIT_COMMIT || "";
const GIT_BRANCH = process.env.NEXT_PUBLIC_GIT_BRANCH || "";
const GIT_DIRTY = process.env.NEXT_PUBLIC_GIT_DIRTY === "true";
const IS_DEV = process.env.NODE_ENV === "development";

function getVersionString(): string {
  const base = `v${VERSION}`;
  const dirty = GIT_DIRTY ? "*" : "";
  const builtWith = "built w/ claude";

  // Dev mode: always show full info + worktree id if present
  if (IS_DEV) {
    const worktree = process.env.NEXT_PUBLIC_WORKTREE ? `-${process.env.NEXT_PUBLIC_WORKTREE}` : "";
    return `${base}${worktree} • dev@${GIT_COMMIT || "local"}${dirty}`;
  }

  // Production: just show version (Vercel builds in detached HEAD)
  // Only show branch info for non-main, non-HEAD branches (preview deploys)
  if (GIT_BRANCH && GIT_BRANCH !== "main" && GIT_BRANCH !== "HEAD" && GIT_COMMIT) {
    return `${base} • ${GIT_BRANCH}@${GIT_COMMIT}`;
  }

  return `${base} • ${builtWith}`;
}

// Get current date in YYYY-MM-DD format
function getBuildDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

interface FooterProps {
  isWhiteTheme?: boolean;
  isLightMode?: boolean;
  brandColor?: string;
  brandColorEnd?: string;
  minimal?: boolean;
  variant?: "default" | "cockpit";
}

export function Footer({
  isWhiteTheme = true,
  isLightMode = false,
  brandColor,
  brandColorEnd,
  minimal = false,
  variant = "default",
}: FooterProps) {
  const lightModeNeutral = isLightMode && isWhiteTheme;

  if (variant === "cockpit") {
    return <CockpitFooter />;
  }

  return (
    <footer
      data-cursor-white
      className={`relative z-10 py-[var(--section-padding-y)] px-[var(--section-padding-x)] ${minimal ? '' : 'border-t border-[var(--surface-border)]'}`}
      style={minimal ? undefined : {
        backgroundColor: "rgba(var(--background-rgb), 0.8)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
      }}
    >
      <div className="max-w-[1200px] mx-auto flex flex-col items-center gap-[var(--space-md)]">
        <Magnetic strength={0.08} tiltStrength={5} padding={320}>
          <MiniCube
            size={80}
            color={brandColor ?? "var(--theme-fg)"}
            isLightMode={isLightMode}
          />
        </Magnetic>
        <Magnetic strength={0.15} tiltStrength={8}>
          <Clock
            className={lightModeNeutral ? "font-mono text-sm tracking-widest" : `font-mono text-sm tracking-widest ${!brandColor ? "gradient-text" : ""}`}
            style={lightModeNeutral ? {
              backgroundImage: "linear-gradient(90deg, #0a0a0a, #444444, #0a0a0a)",
              backgroundSize: "200% 100%",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              animation: "gradient-x 4s linear infinite",
            } : undefined}
            brandColor={!lightModeNeutral ? brandColor : undefined}
            brandColorEnd={!lightModeNeutral ? brandColorEnd : undefined}
          />
        </Magnetic>
        <p className="text-[rgba(var(--foreground-rgb),0.55)] text-[0.75rem] italic tracking-wide">
          Start with empathy, end with impact.
        </p>
        <p className="text-[rgba(var(--foreground-rgb),0.5)] text-[0.7rem] font-mono tracking-wide">
          VN © 2015–2026 • {getVersionString()}
        </p>
      </div>
    </footer>
  );
}

// =============================================================================
// COCKPIT FOOTER
// =============================================================================
// HUD-style footer for spatial OS homepage

function CockpitFooter() {
  const { canAnimate, reducedMotion } = useMotionMode();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number>(0);
  const [mounted, setMounted] = useState(false);
  const [isBOS, setIsBOS] = useState(true); // Track if timezone is BOS

  useEffect(() => {
    setMounted(true);
  }, []);

  // Particle canvas animation
  useEffect(() => {
    if (!canAnimate || reducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = 0;
    let h = 0;

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (!rect) return;
      w = canvas.width = rect.width;
      h = canvas.height = rect.height;
    };

    const makeParticle = (): Particle => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 0.8 + 0.2,
      vx: (Math.random() - 0.5) * 0.07,
      vy: (Math.random() - 0.5) * 0.07,
      a: Math.random() * 0.35 + 0.05,
      ph: Math.random() * Math.PI * 2,
      ps: 0.005 + Math.random() * 0.009,
    });

    const init = () => {
      resize();
      particlesRef.current = Array.from({ length: 120 }, makeParticle);
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      particlesRef.current.forEach(p => {
        p.ph += p.ps;
        const a = p.a * (0.35 + 0.65 * Math.sin(p.ph));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,200,255,${a})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
      });
      rafRef.current = requestAnimationFrame(draw);
    };

    init();
    draw();

    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [canAnimate, reducedMotion]);

  return (
    <footer
      className="relative z-10 overflow-hidden"
      style={{ minHeight: "200px" }}
    >
      {/* No background - starfield shows through */}

      {/* Subtle scanlines overlay */}
      <div
        className="absolute inset-0 pointer-events-none z-[1]"
        style={{
          background: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(var(--foreground-rgb),0.02) 3px, rgba(var(--foreground-rgb),0.02) 4px)",
        }}
      />

      {/* Desktop HUD Grid (>= 768px) */}
      <div
        className="relative z-[2] hidden md:grid min-h-[230px]"
        style={{
          gridTemplateColumns: "1fr auto 1fr",
          gridTemplateRows: "1fr auto auto",
          padding: "22px 28px 18px",
          gap: 0,
        }}
      >
        {/* Left column */}
        <div className="flex flex-col gap-3">
          <HudField label="Origin">
            <span
              className="hud-value font-semibold"
              style={{
                textDecorationLine: isBOS ? "none" : "line-through",
                textDecorationColor: "rgba(var(--foreground-rgb), 0.4)",
                textDecorationStyle: "solid",
              }}
            >
              Boston, MA
            </span>
            <span className="hud-value-dim">42.36°N &nbsp;71.06°W</span>
          </HudField>
          <HudField label="Local Time">
            <Clock
              className="hud-value-bright font-semibold text-[11px] tracking-[0.08em]"
              onTimezoneChange={setIsBOS}
            />
          </HudField>
          <TickBar label="ALT" align="left" pattern={[1,1,2,1,2,1,0,0]} canAnimate={canAnimate} />
        </div>

        {/* Center column - crosshair */}
        <div className="flex flex-col items-center justify-between px-7">
          <div
            className="flex-1"
            style={{
              width: "1px",
              background: "linear-gradient(180deg, transparent, rgba(var(--foreground-rgb), 0.15) 30%, rgba(var(--foreground-rgb), 0.15) 70%, transparent)",
            }}
          />
          <Crosshair canAnimate={canAnimate} />
          <div
            className="flex-1"
            style={{
              width: "1px",
              background: "linear-gradient(180deg, transparent, rgba(var(--foreground-rgb), 0.15) 30%, rgba(var(--foreground-rgb), 0.15) 70%, transparent)",
            }}
          />
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-3 items-end text-right">
          <HudField label="Build">
            <span className="hud-value">{getVersionString()}</span>
            <span className="hud-value-dim">{getBuildDate()}</span>
          </HudField>
          <HudField label="Status">
            <StatusCluster canAnimate={canAnimate} />
          </HudField>
          <TickBar label="HDG" align="right" pattern={[0,1,2,1,2,1,1,1]} canAnimate={canAnimate} />
        </div>

        {/* Edge copy - spans all columns */}
        <div
          className="text-center"
          style={{
            gridColumn: "1 / -1",
            padding: "12px 0 6px",
            borderTop: "1px solid rgba(var(--foreground-rgb), 0.08)",
          }}
        >
          <span
            style={{
              fontSize: "10px",
              color: "rgba(var(--foreground-rgb), 0.3)",
              fontStyle: "italic",
              letterSpacing: "0.04em",
              textShadow: "0 0 12px rgba(var(--foreground-rgb), 0.1)",
            }}
          >
            You&apos;ve drifted to the edge of the map...&nbsp;&nbsp;Nothing beyond here but static.
            <BlinkingCursor canAnimate={canAnimate} />
          </span>
        </div>

      </div>

      {/* Mobile HUD Layout (< 768px) - single column, all centered */}
      <div
        className="relative z-[2] flex flex-col items-center md:hidden min-h-[280px]"
        style={{ padding: "22px 24px 18px" }}
      >
        {/* HUD fields - centered */}
        <div className="flex flex-col items-center text-center gap-3">
          <HudField label="Origin" center>
            <span
              className="hud-value font-semibold"
              style={{
                textDecorationLine: isBOS ? "none" : "line-through",
                textDecorationColor: "rgba(var(--foreground-rgb), 0.4)",
                textDecorationStyle: "solid",
              }}
            >
              Boston, MA
            </span>
            <span className="hud-value-dim">42.36°N &nbsp;71.06°W</span>
          </HudField>
          <HudField label="Local Time" center>
            <Clock
              className="hud-value-bright font-semibold text-[11px] tracking-[0.08em]"
              onTimezoneChange={setIsBOS}
            />
          </HudField>
          <HudField label="Status" center>
            <StatusCluster canAnimate={canAnimate} />
          </HudField>
          <HudField label="Build" center>
            <span className="hud-value">{getVersionString()}</span>
            <span className="hud-value-dim">{getBuildDate()}</span>
          </HudField>
        </div>

        {/* ALT | Crosshair | HDG - in a row */}
        <div className="flex items-center justify-center gap-6 my-6">
          <TickBar label="ALT" align="left" pattern={[1,1,2,1,2,1,0,0]} canAnimate={canAnimate} />
          <Crosshair canAnimate={canAnimate} />
          <TickBar label="HDG" align="right" pattern={[0,1,2,1,2,1,1,1]} canAnimate={canAnimate} />
        </div>

        {/* Edge copy - centered */}
        <div
          className="text-center"
          style={{
            padding: "12px 0 6px",
            borderTop: "1px solid rgba(var(--foreground-rgb), 0.08)",
          }}
        >
          <span
            style={{
              fontSize: "10px",
              color: "rgba(var(--foreground-rgb), 0.3)",
              fontStyle: "italic",
              letterSpacing: "0.04em",
              textShadow: "0 0 12px rgba(var(--foreground-rgb), 0.1)",
            }}
          >
            You&apos;ve drifted to the edge of the map...&nbsp;&nbsp;Nothing beyond here but static.
            <BlinkingCursor canAnimate={canAnimate} />
          </span>
        </div>
      </div>

      {/* Embedded styles for HUD elements - theme aware */}
      <style jsx>{`
        .hud-value {
          font-size: 11px;
          color: rgba(var(--foreground-rgb), 0.7);
          letter-spacing: 0.08em;
          text-shadow: 0 0 8px rgba(var(--foreground-rgb), 0.15);
        }
        .hud-value-dim {
          font-size: 10px;
          color: rgba(var(--foreground-rgb), 0.4);
          letter-spacing: 0.08em;
        }
        .hud-value-bright {
          color: rgba(var(--foreground-rgb), 0.9);
          text-shadow: 0 0 10px rgba(var(--foreground-rgb), 0.2);
        }
      `}</style>
    </footer>
  );
}

// =============================================================================
// COCKPIT SUB-COMPONENTS
// =============================================================================

interface Particle {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  a: number;
  ph: number;
  ps: number;
}

function CornerBracket({ position }: { position: "tl" | "tr" | "bl" | "br" }) {
  const styles: Record<string, React.CSSProperties> = {
    tl: { top: 14, left: 18, borderWidth: "1px 0 0 1px" },
    tr: { top: 14, right: 18, borderWidth: "1px 1px 0 0" },
    bl: { bottom: 14, left: 18, borderWidth: "0 0 1px 1px" },
    br: { bottom: 14, right: 18, borderWidth: "0 1px 1px 0" },
  };

  return (
    <div
      className="absolute"
      style={{
        width: 14,
        height: 14,
        borderColor: "rgba(var(--foreground-rgb), 0.2)",
        borderStyle: "solid",
        ...styles[position],
      }}
    />
  );
}

function HudField({ label, children, center = false }: { label: string; children: React.ReactNode; center?: boolean }) {
  return (
    <div className={`flex flex-col gap-[3px] ${center ? "items-center" : ""}`}>
      <span
        style={{
          fontSize: "8px",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "rgba(var(--foreground-rgb), 0.3)",
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function TickBar({ label, align, pattern, canAnimate = true }: { label: string; align: "left" | "right"; pattern: number[]; canAnimate?: boolean }) {
  // pattern: 0 = off, 1 = on, 2 = hot
  const heights = [4, 5, 7, 6, 8, 5, 4, 3];

  return (
    <div className="flex flex-col gap-1">
      <div
        className="flex items-center gap-[6px]"
        style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
      >
        {align === "left" && (
          <span
            style={{
              fontSize: "7px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(var(--foreground-rgb), 0.25)",
              width: 24,
            }}
          >
            {label}
          </span>
        )}
        <div className="flex items-end" style={{ gap: 2 }}>
          {pattern.map((state, i) => (
            <div
              key={i}
              className={canAnimate && state > 0 ? "tick-animate" : ""}
              style={{
                width: 3,
                height: heights[i],
                borderRadius: 1,
                background: state === 0
                  ? "rgba(var(--foreground-rgb), 0.08)"
                  : state === 1
                    ? "rgba(var(--foreground-rgb), 0.35)"
                    : "rgba(var(--foreground-rgb), 0.55)",
                boxShadow: state === 1
                  ? "0 0 4px rgba(var(--foreground-rgb), 0.15)"
                  : state === 2
                    ? "0 0 6px rgba(var(--foreground-rgb), 0.25)"
                    : "none",
                animation: canAnimate && state > 0
                  ? `tick-pulse ${1.5 + i * 0.3}s ease-in-out ${i * 0.15}s infinite`
                  : "none",
              }}
            />
          ))}
        </div>
        {align === "right" && (
          <span
            style={{
              fontSize: "7px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(var(--foreground-rgb), 0.25)",
              width: 24,
              textAlign: "right",
            }}
          >
            {label}
          </span>
        )}
      </div>
      <style jsx>{`
        @keyframes tick-pulse {
          0%, 100% { opacity: 1; transform: scaleY(1); }
          50% { opacity: 0.6; transform: scaleY(0.85); }
        }
      `}</style>
    </div>
  );
}

function Crosshair({ canAnimate }: { canAnimate: boolean }) {
  return (
    <div
      className="relative flex items-center justify-center shrink-0"
      style={{ width: 30, height: 30 }}
    >
      {/* Horizontal line */}
      <div
        className="absolute"
        style={{
          width: "100%",
          height: 1,
          background: "rgba(var(--foreground-rgb), 0.2)",
          boxShadow: "0 0 6px rgba(var(--foreground-rgb), 0.1)",
        }}
      />
      {/* Vertical line */}
      <div
        className="absolute"
        style={{
          width: 1,
          height: "100%",
          background: "rgba(var(--foreground-rgb), 0.2)",
          boxShadow: "0 0 6px rgba(var(--foreground-rgb), 0.1)",
        }}
      />
      {/* Center dot */}
      <div
        className="relative z-[1] crosshair-dot"
        style={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          background: "rgba(var(--foreground-rgb), 0.3)",
          animation: canAnimate ? "crosshair-dot 4s ease-in-out infinite" : "none",
        }}
      />
      <style jsx>{`
        @keyframes crosshair-dot {
          0%, 100% { opacity: 0.3; box-shadow: none; }
          50% { opacity: 0.7; box-shadow: 0 0 8px rgba(var(--foreground-rgb), 0.3); }
        }
      `}</style>
    </div>
  );
}

function BlinkingCursor({ canAnimate }: { canAnimate: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 5,
        height: 10,
        background: "rgba(var(--foreground-rgb), 0.3)",
        marginLeft: 2,
        verticalAlign: "middle",
        animation: canAnimate ? "cursor-blink 1.4s step-end infinite" : "none",
      }}
    >
      <style jsx>{`
        @keyframes cursor-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </span>
  );
}

function StatusCluster({ canAnimate }: { canAnimate: boolean }) {
  return (
    <div className="flex items-center gap-[10px]">
      {/* Scanning bar */}
      <div
        className="relative overflow-hidden"
        style={{ width: 44, height: 1, background: "rgba(var(--foreground-rgb), 0.1)" }}
      >
        <div
          className="absolute top-0 left-0 h-full"
          style={{
            background: "rgba(var(--foreground-rgb), 0.5)",
            boxShadow: "0 0 4px rgba(var(--foreground-rgb), 0.2)",
            animation: canAnimate ? "scan-bar 5s cubic-bezier(0.4,0,0.6,1) infinite" : "none",
          }}
        />
        <style jsx>{`
          @keyframes scan-bar {
            0% { width: 0; opacity: 0; }
            8% { opacity: 1; }
            70% { width: 100%; opacity: 1; }
            80% { width: 100%; opacity: 0; }
            81%, 100% { width: 0; opacity: 0; }
          }
        `}</style>
      </div>

      {/* Pulse dot */}
      <PulseDot />

      {/* Status label */}
      <span
        style={{
          fontSize: "8px",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "rgba(var(--foreground-rgb), 0.35)",
        }}
      >
        Signal Online
      </span>
    </div>
  );
}
